import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Users } from "lucide-react"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useSubscriptionContext } from "@/contexts/SubscriptionContext"

interface Member {
  id: string
  email: string
  role: string
}

export function WorkspaceMembers({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const { canAddCollaborators } = useSubscriptionContext()
  const [members, setMembers] = useState<Member[]>([])
  const [newMemberEmail, setNewMemberEmail] = useState("")
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (workspaceId && workspaceId !== 'default') {
      fetchMembers()
    }
  }, [workspaceId])

  const fetchMembers = async () => {
    try {
      const { data, error } = await supabase
        .from("workspace_members")
        .select(`
          user_id,
          role,
          profiles:user_id (
            email
          )
        `)
        .eq("workspace_id", workspaceId)

      if (error) throw error

      setMembers(
        data.map((member) => ({
          id: member.user_id,
          email: member.profiles?.email || "",
          role: member.role,
        }))
      )
    } catch (error) {
      console.error("Error fetching members:", error)
      setError(t("common.workspaces.errorFetchingMembers"))
    }
  }

  const addMember = async () => {
    if (!workspaceId || workspaceId === 'default') {
      setError(t("common.workspaces.selectWorkspace"))
      return
    }

    if (!canAddCollaborators()) {
      setError(t("common.workspaces.upgradeToAddMembers"))
      return
    }

    try {
      const { data: userData, error: userError } = await supabase
        .from("profiles")
        .select("id")
        .eq("email", newMemberEmail)
        .single()

      if (userError) throw userError

      const { error: memberError } = await supabase
        .from("workspace_members")
        .insert([
          {
            workspace_id: workspaceId,
            user_id: userData.id,
            role: "member",
          },
        ])

      if (memberError) throw memberError

      toast({
        title: t("common.success"),
        description: t("common.workspaces.memberAdded"),
      })
      setNewMemberEmail("")
      setError(null)
      fetchMembers()
    } catch (error) {
      console.error("Error adding member:", error)
      setError(t("common.workspaces.errorAddingMember"))
    }
  }

  if (!workspaceId || workspaceId === 'default') {
    return null;
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Users className="h-4 w-4 mr-2" />
          {t("common.workspaces.members")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("common.workspaces.manageMembers")}</DialogTitle>
          <DialogDescription>
            {t("common.workspaces.manageMembersDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="flex gap-2">
            <Input
              placeholder={t("common.workspaces.enterEmail")}
              value={newMemberEmail}
              onChange={(e) => setNewMemberEmail(e.target.value)}
            />
            <Button onClick={addMember}>{t("common.add")}</Button>
          </div>
          <div className="space-y-2">
            {members.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-2 bg-secondary/50 rounded-md"
              >
                <span>{member.email}</span>
                <span className="text-sm text-muted-foreground">
                  {member.role}
                </span>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}